import { useCallback, useEffect, useState } from "react";
import { Download, Filter } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageShell, btnGhost, btnPrimary, cellCls, headCls, inputCls } from "@/components/TopBar";
import { fmtEta, fmtKm } from "@/lib/fleet";

const STATUS = ["", "assigned", "in_progress", "parked", "completed", "cancelled"];

const STATUS_COLOR = {
  completed: "#00FF66",
  in_progress: "#00F0FF",
  parked: "#FF3B30",
  cancelled: "#52525B",
  assigned: "#FFCC00",
};

export default function TripHistory() {
  const [trips, setTrips] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({ vehicle_id: "", status: "", date_from: "", date_to: "" });

  const load = useCallback(async (f) => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    const [t, v] = await Promise.all([api.get("/trips", { params }), api.get("/vehicles")]);
    setTrips(t.data);
    setVehicles(v.data);
  }, []);

  useEffect(() => {
    load(filters).catch(() => toast.error("Could not load trips"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = () => load(filters).catch(() => toast.error("Could not load trips"));

  const exportCsv = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await api.get("/trips/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "doot-trips.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const totalKm = trips.reduce((s, t) => s + (t.distance_covered_km || 0), 0);

  return (
    <PageShell
      testid="trips-page"
      subtitle="reporting"
      title="TRIP HISTORY"
      actions={
        <>
          <button data-testid="apply-filters-btn" onClick={apply} className={btnGhost}>
            <Filter size={12} /> APPLY
          </button>
          <button data-testid="export-csv-btn" onClick={exportCsv} className={btnPrimary}>
            <Download size={12} /> EXPORT CSV
          </button>
        </>
      }
    >
      <div className="grid gap-4 border border-white/15 bg-[#121212] p-5 sm:grid-cols-4" data-testid="trip-filters">
        <div>
          <label className="overline mb-2 block">vehicle</label>
          <select
            data-testid="filter-vehicle-select"
            value={filters.vehicle_id}
            onChange={(e) => setFilters({ ...filters, vehicle_id: e.target.value })}
            className={inputCls}
          >
            <option value="">all vehicles</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.plate_number}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="overline mb-2 block">status</label>
          <select
            data-testid="filter-status-select"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className={inputCls}
          >
            {STATUS.map((s) => (
              <option key={s} value={s}>
                {s || "all statuses"}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="overline mb-2 block">from</label>
          <input
            type="date"
            data-testid="filter-date-from"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className={inputCls}
          />
        </div>
        <div>
          <label className="overline mb-2 block">to</label>
          <input
            type="date"
            data-testid="filter-date-to"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className={inputCls}
          />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-6 border-b border-white/10 pb-4">
        <div>
          <p className="overline">trips shown</p>
          <p className="font-head text-3xl font-black" data-testid="trip-count">
            {trips.length}
          </p>
        </div>
        <div>
          <p className="overline">distance covered</p>
          <p className="font-head text-3xl font-black text-[#00FF66]" data-testid="trip-total-km">
            {fmtKm(totalKm)}
            <span className="ml-1 text-[10px] font-normal text-[#52525B]">KM</span>
          </p>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto border border-white/10">
        <table className="w-full min-w-[900px]" data-testid="trips-table">
          <thead className="border-b border-white/10 bg-[#121212]">
            <tr>
              <th className={headCls}>plate</th>
              <th className={headCls}>driver</th>
              <th className={headCls}>route</th>
              <th className={headCls}>status</th>
              <th className={headCls}>planned</th>
              <th className={headCls}>covered</th>
              <th className={headCls}>started</th>
              <th className={headCls}>ended</th>
            </tr>
          </thead>
          <tbody>
            {trips.length === 0 && (
              <tr>
                <td className={`${cellCls} text-[#52525B]`} colSpan={8} data-testid="trips-empty">
                  NO TRIPS FOR THESE FILTERS
                </td>
              </tr>
            )}
            {trips.map((t) => (
              <tr key={t.id} data-testid={`trip-row-${t.id}`} className="border-b border-white/5 hover:bg-[#141414]">
                <td className={`${cellCls} font-head font-bold`}>{t.plate_number}</td>
                <td className={cellCls}>{t.driver_name}</td>
                <td className={`${cellCls} text-[#A1A1AA]`}>
                  <span className="line-clamp-1">
                    {t.origin_name} → {t.destination_name}
                  </span>
                </td>
                <td className={cellCls} style={{ color: STATUS_COLOR[t.status] || "#fff" }}>
                  {t.status.toUpperCase()}
                </td>
                <td className={cellCls}>{fmtKm(t.planned_distance_km)} KM</td>
                <td className={cellCls}>{fmtKm(t.distance_covered_km)} KM</td>
                <td className={`${cellCls} text-[#A1A1AA]`}>{t.started_at ? fmtEta(t.started_at) : "-"}</td>
                <td className={`${cellCls} text-[#A1A1AA]`}>{t.ended_at ? fmtEta(t.ended_at) : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
}
